import {
  createEvidenceCode,
  createIssueCode,
  createRecommendationCode,
  decodeEvidenceProjectPrefix,
  encodeEvidenceProjectId,
  getEvidenceNumberFromCode,
  getEvidenceProjectNumber,
} from "@/lib/entityCode";
import { formatDueDate } from "@/lib/formatDate";
import type {
  EvidenceFile,
  EvidenceItem,
  EvidenceListParams,
  EvidenceOption,
  EvidencePageHeaderData,
  EvidenceProjectOption,
  PaginatedEvidenceListOutput,
} from "@/types";

export {
  createEvidenceCode,
  createIssueCode,
  createRecommendationCode,
  decodeEvidenceProjectPrefix,
  encodeEvidenceProjectId,
  getEvidenceNumberFromCode,
  getEvidenceProjectNumber,
};

export function getNextEvidenceNumberForProject(files: EvidenceItem[], projectLabel: string) {
  const projectNumber = getEvidenceProjectNumber(projectLabel);
  const projectPrefix = encodeEvidenceProjectId(projectNumber);
  const projectFiles = files.filter((file) => file.evidence_code.startsWith(projectPrefix));
  const evidenceNumbers = projectFiles.map((file) => getEvidenceNumberFromCode(file.evidence_code));

  return Math.max(0, ...evidenceNumbers) + 1;
}

function createEvidenceDueDate(
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number
) {
  return formatDueDate(new Date(year, monthIndex, day, hour, minute));
}

function createProjectStandardCode(standard: string) {
  const normalizedStandard = standard.trim().toUpperCase();
  const fallbackCharacter = normalizedStandard.at(-1) ?? "X";

  return normalizedStandard.slice(0, 4).padEnd(4, fallbackCharacter);
}

function createProjectLabel(
  standard: string,
  clientCode: string,
  siteCode: string,
  serial: string
) {
  return `${createProjectStandardCode(standard)}-${clientCode}-${siteCode}-${serial}`;
}

const primaryEvidenceProjectLabel = createProjectLabel("GISTM", "VAL", "CON", "001");
const secondaryEvidenceProjectLabel = createProjectLabel("GISTM", "VAL", "FAB", "002");

export const evidenceProjectOptions: EvidenceProjectOption[] = [
  {
    value: "gistm-val-con-001",
    label: primaryEvidenceProjectLabel,
    client: "Vale",
    standard: "GISTM",
    site: "Conceição",
    assets: ["Conceição", "Itabiruçu"],
    projectManager: "James Carter",
    status: "assessment",
  },
  {
    value: "gist-val-fab-002",
    label: secondaryEvidenceProjectLabel,
    client: "Vale",
    standard: "GISTM",
    site: "Fábrica",
    assets: ["Forquilha I", "Forquilha II", "Forquilha III", "Forquilha IV", "Forquilha V"],
    projectManager: "Sarah Mitchell",
    status: "completed",
  },
  {
    value: "gist-val-fab-003",
    label: createProjectLabel("ANCOLD", "VAL", "FAB", "003"),
    client: "Vale",
    standard: "ANCOLD",
    site: "Forquilha II",
    assets: ["Forquilha II"],
    projectManager: "Michael Reed",
    status: "on_hold",
  },
  {
    value: "gist-bhp-mng-001",
    label: createProjectLabel("GISTM", "BHP", "MNG", "001"),
    client: "BHP",
    standard: "GISTM",
    site: "Mina Norte",
    projectManager: "Robert Hayes",
    status: "assessment",
  },
  {
    value: "gist-bhp-mng-002",
    label: createProjectLabel("ICOLD", "BHP", "MNG", "002"),
    client: "BHP",
    standard: "ICOLD",
    site: "Mina Sul",
    projectManager: "Emily Turner",
    status: "completed",
  },
  {
    value: "gist-ant-cer-003",
    label: createProjectLabel("MAC", "ANT", "CER", "003"),
    client: "Anglo American",
    standard: "MAC",
    site: "Cerro Azul",
    projectManager: "Daniel Brooks",
    status: "assessment",
  },
  {
    value: "gist-ant-cer-004",
    label: createProjectLabel("GISTM", "ANT", "CER", "004"),
    client: "Anglo American",
    standard: "GISTM",
    site: "Cerro Verde",
    projectManager: "James Carter",
    status: "signatures",
  },
  {
    value: "gist-sam-itb-002",
    label: createProjectLabel("ICOLD", "SAM", "ITB", "002"),
    client: "Samarco",
    standard: "ICOLD",
    site: "Itabirito",
    projectManager: "Sarah Mitchell",
    status: "completed",
  },
  {
    value: "gist-sam-mar-001",
    label: createProjectLabel("ANCOLD", "SAM", "MAR", "001"),
    client: "Samarco",
    standard: "ANCOLD",
    site: "Mariana",
    projectManager: "Michael Reed",
    status: "report",
  },
  {
    value: "gist-csn-cas-001",
    label: createProjectLabel("MAC", "CSN", "CAS", "001"),
    client: "CSN",
    standard: "MAC",
    site: "Casa de Pedra",
    projectManager: "Robert Hayes",
    status: "on_hold",
  },
  {
    value: "gist-vbm-ser-001",
    label: createProjectLabel("GISTM", "VBM", "SER", "001"),
    client: "Vale Base Metals",
    standard: "GISTM",
    site: "Serra Leste",
    projectManager: "Emily Turner",
    status: "assessment",
  },
  {
    value: "gist-vbm-sos-002",
    label: createProjectLabel("ICOLD", "VBM", "SOS", "002"),
    client: "Vale Base Metals",
    standard: "ICOLD",
    site: "Sossego",
    projectManager: "Daniel Brooks",
    status: "completed",
  },
  ...Array.from({ length: 32 }, (_, index) => {
    const seed = index + 13;
    const clientCycle = [
      { label: "Vale", code: "VAL" },
      { label: "BHP", code: "BHP" },
      { label: "Anglo American", code: "ANT" },
      { label: "Samarco", code: "SAM" },
      { label: "CSN", code: "CSN" },
      { label: "Vale Base Metals", code: "VBM" },
      { label: "Nexa", code: "NEX" },
      { label: "Hydro", code: "HYD" },
    ] as const;
    const standardCycle = ["GISTM", "ICOLD", "ANCOLD", "MAC", "CDA", "TSM"] as const;
    const siteCycle = [
      { label: "Forquilha III", code: "FQ3" },
      { label: "Forquilha IV", code: "FQ4" },
      { label: "Mina Leste", code: "MLE" },
      { label: "Mina Oeste", code: "MOE" },
      { label: "Serra Azul", code: "SAZ" },
      { label: "Brucutu", code: "BRC" },
      { label: "Cauê", code: "CAU" },
      { label: "Alegria", code: "ALG" },
      { label: "Conceição", code: "CON" },
      { label: "Salobo", code: "SAL" },
      { label: "Onça Puma", code: "ONP" },
      { label: "Barro Alto", code: "BAL" },
    ] as const;
    const managerCycle = [
      "James Carter",
      "Sarah Mitchell",
      "Michael Reed",
      "Robert Hayes",
      "Emily Turner",
      "Daniel Brooks",
      "Olivia Bennett",
      "Lucas Foster",
    ] as const;
    const statusCycle = [
      "assessment",
      "completed",
      "on_hold",
      "report",
      "signatures",
      "canceled",
    ] as const;

    const client = clientCycle[index % clientCycle.length];
    const site = siteCycle[index % siteCycle.length];
    const standard = standardCycle[index % standardCycle.length];
    const projectManager = managerCycle[index % managerCycle.length];
    const status = statusCycle[index % statusCycle.length];
    const serial = String(seed).padStart(3, "0");

    return {
      value: `gist-${client.code.toLowerCase()}-${site.code.toLowerCase()}-${serial}`,
      label: createProjectLabel(standard, client.code, site.code, serial),
      client: client.label,
      standard,
      site: site.label,
      assets: [site.label],
      projectManager,
      status,
    } satisfies EvidenceProjectOption;
  }),
];

export const evidencePageHeader: EvidencePageHeaderData = {
  title: "Evidence",
  count: 98,
  breadcrumbs: [primaryEvidenceProjectLabel, "GISTM", "Vale", "Conceição"],
  areas: ["Conceição", "Itabiruçu"],
};

export const evidenceTypeOptions: EvidenceOption[] = [
  { value: "desk-review", label: "Desk Review", tag: "Interview" },
  { value: "field-inspection", label: "Field Inspection", tag: "Interview" },
  { value: "interview", label: "Interview", tag: "Interview" },
  { value: "classified", label: "Classified", tag: "Restricted" },
];

export const evidencePrincipleOptions: EvidenceOption[] = [
  { value: "all", label: "All" },
  ...Array.from({ length: 15 }, (_, index) => {
    const principle = String(index + 1);

    return {
      value: principle,
      label: principle,
      tag: Number(principle) <= 3 ? "Interview" : Number(principle) === 4 ? "Visual" : undefined,
    };
  }),
];

export const evidenceEditPrincipleOptions = evidencePrincipleOptions.filter(
  (option) => option.value !== "all"
);

export const evidenceRequirementOptionsByPrinciple: Record<string, EvidenceOption[]> = {
  "1": [
    { value: "1.1", label: "1.1", tag: "Interview" },
    { value: "1.2", label: "1.2", tag: "Interview" },
  ],
  "2": [
    { value: "2.1", label: "2.1", tag: "Interview" },
    { value: "2.2", label: "2.2", tag: "Interview" },
  ],
  "3": [
    { value: "3.1", label: "3.1", tag: "Interview" },
    { value: "3.2", label: "3.2", tag: "Interview" },
    { value: "3.3", label: "3.3", tag: "Interview" },
    { value: "3.4", label: "3.4", tag: "Interview" },
    { value: "3.5", label: "3.5", tag: "Interview" },
  ],
  "4": [
    { value: "4.1", label: "4.1", tag: "Visual" },
    { value: "4.2", label: "4.2", tag: "Visual" },
  ],
};

export const evidenceRequirementFilterOptions: EvidenceOption[] = [
  { value: "all", label: "All" },
  ...evidenceRequirementOptionsByPrinciple["3"],
];

export const mockEvidenceFiles: EvidenceItem[] = [
  {
    evidence_id: "evidence-1",
    evidence_code: createEvidenceCode(1),
    filename: "EIA_Fabrica.pdf",
    file_extension: "pdf",
    uploaded_by: "James Carter",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 8, 59),
    type: "Desk Review",
    referenced: true,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "3", evaluation_level_codes: ["3.1", "3.2"] }],
  },
  {
    evidence_id: "evidence-2",
    evidence_code: createEvidenceCode(1, getEvidenceProjectNumber(secondaryEvidenceProjectLabel)),
    filename: "Wyvern_Assessment.png",
    file_extension: "png",
    uploaded_by: "Michael Reed",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 9, 15),
    type: "Interview",
    referenced: false,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "2", evaluation_level_codes: ["2.1"] }],
  },
  {
    evidence_id: "evidence-3",
    evidence_code: createEvidenceCode(2),
    filename: "ProjectPhoenix_Rpt.jpeg",
    file_extension: "jpeg",
    uploaded_by: "Sarah Mitchell",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 10, 24),
    type: "Field Inspection",
    referenced: false,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "3", evaluation_level_codes: ["3.3"] }],
  },
  {
    evidence_id: "evidence-4",
    evidence_code: createEvidenceCode(3),
    filename: "Chimera_Study.m4a",
    file_extension: "m4a",
    uploaded_by: "Robert Hayes",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 11, 42),
    type: "Classified",
    referenced: true,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "3", evaluation_level_codes: ["3.4"] }],
  },
  {
    evidence_id: "evidence-5",
    evidence_code: createEvidenceCode(4),
    filename: "Hydra_ImpactReport.pdf",
    file_extension: "pdf",
    uploaded_by: "Emily Turner",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 1, 5),
    type: "Desk Review",
    referenced: false,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "3", evaluation_level_codes: ["3.5"] }],
  },
  {
    evidence_id: "evidence-6",
    evidence_code: createEvidenceCode(5),
    filename: "Manticore_Findings.png",
    file_extension: "png",
    uploaded_by: "Daniel Brooks",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 2, 18),
    type: "Interview",
    referenced: false,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "1", evaluation_level_codes: ["1.2"] }],
  },
  {
    evidence_id: "evidence-7",
    evidence_code: createEvidenceCode(6),
    filename: "Griffin_Analysis.jpeg",
    file_extension: "jpeg",
    uploaded_by: "James Carter",
    uploaded_at: createEvidenceDueDate(2026, 1, 20, 3, 27),
    type: "Field Inspection",
    referenced: false,
    tags: ["Environmental", "Foundation"],
    linked_level: [{ grouping_level_code: "4", evaluation_level_codes: ["4.1"] }],
  },
  ...Array.from({ length: 92 }, (_, index) => {
    const seed = index + 8;
    const extensions = ["pdf", "png", "jpeg", "m4a", "docx", "xlsx"] as const;
    const typeCycle = ["Desk Review", "Interview", "Field Inspection", "Classified"] as const;
    const ownerCycle = [
      "James Carter",
      "Michael Reed",
      "Sarah Mitchell",
      "Robert Hayes",
      "Emily Turner",
      "Daniel Brooks",
    ] as const;
    const areaCycle = [
      "Forquilha",
      "Vale",
      "Fabrica",
      "Phoenix",
      "Hydra",
      "Manticore",
      "Griffin",
    ] as const;
    const subjectCycle = [
      "Baseline",
      "Survey",
      "Checklist",
      "Overview",
      "Register",
      "Inspection",
      "Summary",
      "Archive",
    ] as const;
    const tagCycle = [
      ["Environmental", "Foundation"],
      ["Operations", "Safety"],
      ["Geotechnical", "Monitoring"],
      ["Community", "Engagement"],
    ] as const;
    const minuteCycle = [5, 12, 18, 24, 31, 37, 44, 51, 59] as const;
    const requirementCycle = [
      { principle: "1", requirement: "1.1", tag: "Interview" },
      { principle: "1", requirement: "1.2", tag: "Interview" },
      { principle: "2", requirement: "2.1", tag: "Interview" },
      { principle: "2", requirement: "2.2", tag: "Interview" },
      { principle: "3", requirement: "3.1", tag: "Interview" },
      { principle: "3", requirement: "3.2", tag: "Interview" },
      { principle: "3", requirement: "3.3", tag: "Interview" },
      { principle: "3", requirement: "3.4", tag: "Interview" },
      { principle: "3", requirement: "3.5", tag: "Interview" },
      { principle: "4", requirement: "4.1", tag: "Visual" },
      { principle: "4", requirement: "4.2", tag: "Visual" },
    ] as const;

    const extension = extensions[index % extensions.length];
    const type = typeCycle[index % typeCycle.length];
    const requirement = requirementCycle[index % requirementCycle.length];
    const secondaryRequirement = requirementCycle[(index + 3) % requirementCycle.length];
    const owner = ownerCycle[index % ownerCycle.length];
    const tags = [...tagCycle[index % tagCycle.length]];
    const area = areaCycle[index % areaCycle.length];
    const subject = subjectCycle[index % subjectCycle.length];
    const referenced = seed === 78 || seed % 11 === 0;

    const linked_level = [
      {
        grouping_level_code: requirement.principle,
        evaluation_level_codes: [requirement.requirement],
      },
      ...(index % 3 === 0
        ? [
            {
              grouping_level_code: secondaryRequirement.principle,
              evaluation_level_codes: [secondaryRequirement.requirement],
            },
          ]
        : []),
    ];

    const generatedFilename = `${area}_${subject}_${String(seed).padStart(2, "0")}.${extension}`;

    return {
      evidence_id: `evidence-${seed}`,
      evidence_code: createEvidenceCode(seed - 1),
      filename: generatedFilename,
      file_extension: extension,
      uploaded_by: owner,
      uploaded_at: createEvidenceDueDate(
        2026,
        1,
        (index % 9) + 20,
        (index % 10) + 8,
        minuteCycle[index % minuteCycle.length]
      ),
      type,
      referenced,
      tags,
      linked_level,
    } satisfies EvidenceItem;
  }),
];

export function getMockEvidenceFiles(params?: EvidenceListParams): PaginatedEvidenceListOutput {
  const page = params?.page ?? 1;
  const pageSize = params?.page_size ?? 25;

  const filteredFiles = mockEvidenceFiles.filter((file) => {
    if (
      params?.principle_codes?.length &&
      !file.linked_level.some((req) => params.principle_codes!.includes(req.grouping_level_code))
    ) {
      return false;
    }
    if (
      params?.requirement_codes?.length &&
      !file.linked_level.some((req) =>
        req.evaluation_level_codes.some((code) => params.requirement_codes!.includes(code))
      )
    ) {
      return false;
    }
    if (params?.file_extensions?.length) {
      const ext = file.filename.split(".").pop()?.toLowerCase() ?? "";
      if (!params.file_extensions.includes(ext)) return false;
    }
    if (params?.evidence_types?.length && !params.evidence_types.includes(file.type)) {
      return false;
    }
    if (params?.referenced !== undefined && file.referenced !== params.referenced) {
      return false;
    }

    return true;
  });

  const total = filteredFiles.length;
  const start = (page - 1) * pageSize;
  const items = filteredFiles.slice(start, start + pageSize);

  return {
    total_items: total,
    current_page: page,
    page_size: pageSize,
    total_pages: Math.ceil(total / pageSize),
    items,
    grouping_level_name: "",
    evaluation_level_name: "",
  };
}

export const mockEvidenceFileDetail: EvidenceFile = {
  evidence_id: "evidence-1",
  evidence_code: createEvidenceCode(1),
  filename: "EIA_Fabrica.pdf",
  file_extension: "pdf",
  uploaded_by: "James Carter",
  uploaded_at: createEvidenceDueDate(2026, 1, 20, 8, 59),
  type: "Desk Review",
  referenced: true,
  tags: ["Environmental", "Foundation"],
  notes: "Environmental impact assessment for the Fabrica site.",
  file_size: "1.5 MB",
  grouping_level_name: "Principle",
  evaluation_level_name: "Requirement",
  linked_level: [
    {
      grouping_node_id: 1,
      grouping_level_code: "3",
      evaluation_levels: [
        { node_id: 1, evaluation_level_code: "3.1", is_protected: false },
        { node_id: 2, evaluation_level_code: "3.2", is_protected: false },
      ],
    },
  ],
  assessment_references: [],
  permissions: {
    can_download: true,
    can_edit: true,
    can_delete: true,
  },
};

export function getEvidenceFileTypeOptions(files: EvidenceItem[]): EvidenceOption[] {
  const collator = new Intl.Collator("en", { sensitivity: "base" });
  const extensions = Array.from(
    new Set(
      files
        .map((file) => file.filename.split(".").pop()?.toLowerCase())
        .filter((extension): extension is string => Boolean(extension))
    )
  ).sort((left, right) => collator.compare(left, right));

  return [
    { value: "all", label: "All" },
    ...extensions.map((extension) => ({
      value: extension,
      label: extension,
    })),
  ];
}
